<template>
  <SettingsCard
    :title="$t('settings.company.website.title')"
    :subtitle="$t('settings.company.website.subtitle')"
    icon="mdi-web"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="px-4 pt-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">
                {{ $t('settings.company.website.body-texts.0') }}
              </span>
            </div>
            <span class="text-grey">
              {{ $t('settings.company.website.body-texts.1') }}
              <br />
              {{ $t('settings.company.website.body-texts.2') }}
            </span>
          </div>
        </v-col>

        <v-col
          cols="12"
          sm="6"
          class="justify-center px-0 mt-4 d-flex px-md-2"
        >
          <LinkSettings
            :links="links"
            @update-settings="saveSettings"
          />
        </v-col>
      </v-row>

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.COMPANY"
      >
        <v-row v-if="feature.config.isSlugEditable">
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.company.website.body-texts-light.0') }}
                </span>
              </div>
              <div class="text-grey">
                <p>{{ $t('settings.company.website.body-texts-light.1') }}</p>
                <p v-html="$t('settings.company.website.body-texts-light.2')" />
                <p>{{ $t('settings.company.website.body-texts-light.3') }}</p>
              </div>
            </div>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            class="justify-center px-0 mt-4 d-flex px-md-2"
          >
            <SlugData
              v-bind="companySlug"
              :parent-error="$t(slugError ?? '')"
              :parent-success="$t(slugSuccess ?? '')"
              @update-slug="saveSlug"
            />
          </v-col>
        </v-row>
      </Feature>
    </div>
  </SettingsCard>
</template>

<script>
import SlugData from '@/components/forms/SlugData'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import bus, { eventNames } from '@/lib/eventBus'
import { formatHttpsUrl } from '@/lib/urlFormatter'
import feature from '@/mixins/feature'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import LinkSettings from './LinkSettings'
import UPDATE_COMPANY_LINKS from './queries/UpdateCompanyLinks.gql'
import UPDATE_COMPANY_SLUG from './queries/UpdateCompanySlug.gql'

const ERROR_TYPES = {
  PERMISSION: 'Missing permission',
  SLUG_TAKEN: 'Slug already taken',
  VALIDATION: 'Expected',
}

const ERROR_MESSAGES = {
  [ERROR_TYPES.PERMISSION]: 'alerts.settings.company.slug-data.error-permission',
  [ERROR_TYPES.SLUG_TAKEN]: 'alerts.settings.company.slug-data.error-taken',
  [ERROR_TYPES.VALIDATION]: 'alerts.settings.company.slug-data.error-blacklist',
  DEFAULT: 'alerts.settings.company.slug-data.error',
}

export default {
  name: 'WebSettings',
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'web' })
    return {
      isOpen,
      setOpen,
    }
  },

  components: {
    SettingsCard,
    LinkSettings,
    SlugData,
  },

  mixins: [feature],

  props: {
    company: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    settings: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },

  data() {
    return {
      links: this.company.links,
      slugError: null,
      slugSuccess: null,
    }
  },

  computed: {
    companySlug() {
      return {
        slug: this.company.slug,
      }
    },
  },

  methods: {
    handleError(error, type = 'DEFAULT') {
      const errorMessage = ERROR_MESSAGES[type] || ERROR_MESSAGES.DEFAULT
      this.slugError = errorMessage
      bus.$emit(eventNames.SHOW_SNACKBAR, {
        color: 'error',
        text: this.$t(errorMessage),
      })
    },

    getErrorType(errorMessage) {
      for (const [key, value] of Object.entries(ERROR_TYPES)) {
        if (errorMessage.match(value)) return key
      }
      return 'DEFAULT'
    },

    async saveSettings(linksData) {
      const linksToUpdate = Object.fromEntries(
        Object.entries(linksData).filter(([key]) => key !== '__typename')
      )
      const formattedLinks = Object.entries(linksToUpdate).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value ? formatHttpsUrl(value) : null,
        }),
        {}
      )

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_LINKS,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              links: formattedLinks,
            },
          },
          refetchQueries: [
            {
              query: PENDING_CHANGES_FOR_REGENERATION,
              variables: { companyId: this.company.id },
            },
          ],
        })

        this.links = formattedLinks
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.company.success'),
        })
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.company.error'),
        })
      }
    },

    async saveSlug(slug) {
      this.slugError = null
      this.slugSuccess = null

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_SLUG,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              newSlug: slug,
            },
          },
          refetchQueries: [
            {
              query: PENDING_CHANGES_FOR_REGENERATION,
              variables: { companyId: this.company.id },
            },
          ],
        })

        const successMessage = 'alerts.settings.company.slug-data.success'
        this.slugSuccess = successMessage
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t(successMessage),
        })
      } catch (error) {
        this.handleError(error.message, this.getErrorType(error.message))
      }
    },
  },
}
</script>
